import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * AdminHealthPanel — Observability for emails, push, webhooks, and cron jobs.
 * Calls RPC admin_health_stats(hours). Surfaces failure counts + recent errors.
 */
export function AdminHealthPanel({ lang, toast }: { lang: string; toast?: (m: string) => void }) {
  const es = lang === "es";
  const [hours, setHours] = useState(24);
  const [data, setData] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: d, error } = await (supabase as any).rpc("admin_health_stats", { _hours: hours });
    setLoading(false);
    if (error) { toast?.(error.message); return; }
    setData(d);
  }, [hours, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  const alerts = useMemo(() => {
    if (!data) return [] as { sev: "warn" | "err"; msg: string }[];
    const out: { sev: "warn" | "err"; msg: string }[] = [];
    const e = data.emails || {}; const p = data.push || {}; const w = data.webhooks || {};
    const failRate = (f: number, t: number) => (t > 0 ? (f / t) * 100 : 0);
    if ((e.failed || 0) > 0) out.push({ sev: failRate(e.failed, e.total) > 10 ? "err" : "warn", msg: `${e.failed} emails fallidos (${failRate(e.failed, e.total).toFixed(1)}%)` });
    if ((p.failed || 0) > 0) out.push({ sev: failRate(p.failed, p.total) > 20 ? "err" : "warn", msg: `${p.failed} push fallidos` });
    if ((w.failed || 0) > 0) out.push({ sev: failRate(w.failed, w.total) > 5 ? "err" : "warn", msg: `${w.failed} webhooks fallidos` });
    if ((w.pending || 0) > 10) out.push({ sev: "warn", msg: `${w.pending} webhooks pendientes` });
    for (const c of (data.cron || [])) {
      if (c.last_status === "failed") out.push({ sev: "err", msg: `Cron "${c.jobname}" última ejecución falló` });
      if ((c.recent_failures || 0) > 0) out.push({ sev: "warn", msg: `Cron "${c.jobname}": ${c.recent_failures} fallos recientes` });
      if (c.active === false) out.push({ sev: "warn", msg: `Cron "${c.jobname}" inactivo` });
    }
    return out;
  }, [data]);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="card" style={{ padding: 14, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0 }}>{es ? "Panel de salud" : "Health panel"}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <select className="input" value={hours} onChange={e => setHours(Number(e.target.value))}>
            <option value={1}>1h</option><option value={6}>6h</option>
            <option value={24}>24h</option><option value={72}>72h</option>
            <option value={168}>7d</option>
          </select>
          <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            {es ? "Auto 30s" : "Auto 30s"}
          </label>
          <button className="btn" onClick={load} disabled={loading}>{loading ? "…" : (es ? "Refrescar" : "Refresh")}</button>
        </div>
      </div>

      {alerts.length > 0 && (
        <div className="card" style={{ padding: 14, borderLeft: "4px solid var(--am)" }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{es ? "Alertas activas" : "Active alerts"}</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {alerts.map((a, i) => (
              <li key={i} style={{ color: a.sev === "err" ? "var(--am)" : "var(--mu)" }}>{a.msg}</li>
            ))}
          </ul>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
        <StatCard title={es ? "Emails" : "Emails"} stats={data?.emails} keys={["total","sent","failed","suppressed","pending"]} />
        <StatCard title="Push" stats={data?.push} keys={["total","sent","failed","expired"]} />
        <StatCard title="Webhooks" stats={data?.webhooks} keys={["total","delivered","failed","pending"]} />
        <div className="card" style={{ padding: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>{es ? "Cron jobs" : "Cron jobs"}</div>
          <div style={{ fontSize: 13 }}>{(data?.cron || []).length} {es ? "activos" : "registered"}</div>
        </div>
      </div>

      <Section title={es ? "Cron jobs" : "Cron jobs"}>
        <table className="tbl">
          <thead><tr>
            <th>{es ? "Job" : "Job"}</th><th>{es ? "Horario" : "Schedule"}</th>
            <th>{es ? "Estado" : "Status"}</th><th>{es ? "Última ejecución" : "Last run"}</th>
            <th>{es ? "Fallos recientes" : "Recent failures"}</th>
          </tr></thead>
          <tbody>
            {(data?.cron || []).map((c: any) => (
              <tr key={c.jobname}>
                <td>{c.jobname}{c.active === false && <span style={{ marginLeft: 6, color: "var(--am)" }}>·{es ? "pausado" : "paused"}</span>}</td>
                <td style={{ fontFamily: "monospace", fontSize: 11 }}>{c.schedule}</td>
                <td><StatusBadge status={c.last_status} /></td>
                <td style={{ fontSize: 11, color: "var(--mu)" }}>{c.last_start ? new Date(c.last_start).toLocaleString() : "—"}</td>
                <td style={{ color: (c.recent_failures || 0) > 0 ? "var(--am)" : undefined }}>{c.recent_failures ?? 0}</td>
              </tr>
            ))}
            {!data?.cron?.length && <tr><td colSpan={5} style={{ textAlign: "center", color: "var(--mu)", padding: 12 }}>—</td></tr>}
          </tbody>
        </table>
      </Section>

      <Section title={es ? "Últimos errores de email" : "Recent email errors"}>
        <FailureList rows={data?.email_failures} cols={["template","recipient","error"]} />
      </Section>
      <Section title={es ? "Últimos errores de push" : "Recent push errors"}>
        <FailureList rows={data?.push_failures} cols={["topic","title","error"]} />
      </Section>
      <Section title={es ? "Últimos errores de webhooks" : "Recent webhook errors"}>
        <FailureList rows={data?.webhook_failures} cols={["event","http_status","attempts","error"]} />
      </Section>
    </div>
  );
}

function StatCard({ title, stats, keys }: { title: string; stats: any; keys: string[] }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, fontSize: 13 }}>
        {keys.map(k => (
          <div key={k} style={{ display: "flex", justifyContent: "space-between" }}>
            <span style={{ color: "var(--mu)" }}>{k}</span>
            <b style={{ color: k === "failed" && (stats?.[k] || 0) > 0 ? "var(--am)" : undefined }}>{stats?.[k] ?? 0}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <div style={{ overflowX: "auto" }}>{children}</div>
    </div>
  );
}

function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return <span style={{ color: "var(--mu)" }}>—</span>;
  const ok = ["succeeded","sent","delivered"].includes(status);
  const bad = ["failed","dlq","error"].includes(status);
  return <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: ok ? "#dcfce7" : bad ? "#fee2e2" : "#fef3c7", color: ok ? "#166534" : bad ? "#991b1b" : "#92400e" }}>{status}</span>;
}

function FailureList({ rows, cols }: { rows: any[] | undefined; cols: string[] }) {
  if (!rows || rows.length === 0) return <div style={{ color: "var(--mu)", fontSize: 13 }}>—</div>;
  return (
    <table className="tbl">
      <thead><tr>{cols.map(c => <th key={c}>{c}</th>)}<th>when</th></tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {cols.map(c => (
              <td key={c} style={{ fontSize: 12, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis" }}>
                {String(r[c] ?? "—")}
              </td>
            ))}
            <td style={{ fontSize: 11, color: "var(--mu)" }}>{r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}